import React, { memo, useMemo, useState } from 'react';
import { Button, Stack } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PickerMenu from '../../../shared/components/PickerMenu';
import type { PickerItem } from '../../../shared/components/PickerMenu';
import type { WorkflowCatalogEntry, WorkflowRunSummary } from '../../../types/backend';

export interface PlaybookChoice {
  prompt_id: string;
  title: string;
  description?: string;
  /** The version to pin, and whose prompt it is. A set needs both. */
  version: string;
  owner: string;
}

interface Props {
  playbooks: PlaybookChoice[];
  playbookId?: string;
  playbookTitle?: string;
  onChoosePlaybook?: (promptId: string | null) => void;
  onReadPlaybook?: () => void;
  onRunPlaybook?: () => void;
  playbookExecuted?: boolean;
  workflows: WorkflowCatalogEntry[];
  runs: WorkflowRunSummary[];
  onRunWorkflow: (workflowName: string) => void;
  onOpenRun: (runId: string) => void;
  /** Workflows are behind a switch; a playbook is not, so only that half goes away. */
  workflowsEnabled?: boolean;
}

// What this case is working to, in the two words the product actually uses, sitting where the
// operator is already looking: just above the message box.
//
// They are two different kinds of thing and the controls say so rather than leaving it to a
// tooltip. A playbook is guidance the agent reads and judges, so there is one at a time and its
// list is a radio list. A workflow is steps the runtime enforces, so you run as many as the case
// needs and its list offers each one to run or to open.
//
// Both lists are the same component. They differ in what they hold, never in how choosing works.
const CaseControls = memo<Props>(({
  playbooks,
  playbookId,
  playbookTitle,
  onChoosePlaybook,
  onReadPlaybook,
  onRunPlaybook,
  playbookExecuted = false,
  workflows,
  runs,
  onRunWorkflow,
  onOpenRun,
  workflowsEnabled = true,
}) => {
  const [playbookAnchor, setPlaybookAnchor] = useState<HTMLElement | null>(null);
  const [workflowAnchor, setWorkflowAnchor] = useState<HTMLElement | null>(null);

  const canChoosePlaybook = Boolean(onChoosePlaybook) && playbooks.length > 0;

  // The two acts on the attached playbook sit at the top of its own list, because that is where
  // an operator goes looking for the thing the case is already following.
  const playbookItems = useMemo<PickerItem[]>(() => {
    const head: PickerItem[] = [];
    if (playbookTitle && onReadPlaybook) {
      head.push({ id: '__read', title: `Read ${playbookTitle}`, selectable: false });
    }
    if (playbookTitle && !playbookExecuted && onRunPlaybook) {
      head.push({
        id: '__run',
        title: 'Tell the agent to follow it now',
        selectable: false,
      });
    }
    return [
      ...head,
      ...playbooks.map((playbook) => ({
        id: playbook.prompt_id,
        title: playbook.title,
        description: playbook.description,
      })),
    ];
  }, [playbooks, playbookTitle, playbookExecuted, onReadPlaybook, onRunPlaybook]);

  // A run is addressed by the workflow it runs, so a workflow already running offers Open and not
  // a second Run: starting the same workflow twice by accident is the mistake this prevents.
  const runByWorkflow = useMemo(
    () => new Map(runs.map((run) => [run.workflow_name, run])),
    [runs],
  );

  const workflowItems = useMemo<PickerItem[]>(
    () =>
      workflows.map((workflow) => ({
        id: workflow.name,
        title: workflow.title,
        // The size of the thing, so running one is not a blind commitment.
        description: `${workflow.node_count} steps · ${workflow.layer_count} stages`,
        action: runByWorkflow.has(workflow.name) ? 'Open' : 'Run',
      })),
    [workflows, runByWorkflow],
  );

  return (
    <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={!canChoosePlaybook && !playbookTitle}
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setPlaybookAnchor(e.currentTarget)}
        sx={{ textTransform: 'none' }}
      >
        Playbook: {playbookTitle ?? 'none'}
      </Button>

      {workflowsEnabled && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          endIcon={<ArrowDropDownIcon />}
          onClick={(e) => setWorkflowAnchor(e.currentTarget)}
          sx={{ textTransform: 'none' }}
        >
          {runs.length === 0 ? 'Run a workflow' : `Workflows: ${runs.length}`}
        </Button>
      )}

      <PickerMenu
        anchorEl={playbookAnchor}
        onClose={() => setPlaybookAnchor(null)}
        caption="One at a time. The agent reads it."
        items={playbookItems}
        selectedId={playbookId ?? null}
        onPick={(id) => {
          if (id === '__read') onReadPlaybook?.();
          else if (id === '__run') onRunPlaybook?.();
          else onChoosePlaybook?.(id);
        }}
        clearLabel={canChoosePlaybook ? 'No playbook' : undefined}
        onClear={canChoosePlaybook ? () => onChoosePlaybook?.(null) : undefined}
        emptyText="No playbooks available"
      />

      <PickerMenu
        anchorEl={workflowAnchor}
        onClose={() => setWorkflowAnchor(null)}
        caption="Run as many as the case needs."
        items={workflowItems}
        onPick={(name) => {
          const run = runByWorkflow.get(name);
          if (run) onOpenRun(run.run_id);
          else onRunWorkflow(name);
        }}
        emptyText="No workflows available"
      />
    </Stack>
  );
});

CaseControls.displayName = 'CaseControls';

export default CaseControls;
